<template>
  <b-modal
    id="goodsReceiveDetailModal"
    ref="goodsReceiveDetailModal"
    size="xl"
    title="ใบรับสินค้า"
    no-close-on-backdrop
    @close="close"
    hide-footer
  >
    <div
      v-if="isLoading"
      class="text-center py-4"
    >
      <b-spinner variant="primary"></b-spinner>
    </div>
    <div
      v-else
      class="gr-detail-layout"
    >
      <!-- Left: metadata -->
      <div class="gr-detail-info">
        <div class="info-row">
          <span class="info-label">เลขที่เอกสาร:</span>
          <span class="info-value">{{ data.grCode }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">รหัสใบสั่งซื้ออ้างอิง:</span>
          <span class="info-value">{{ data.poRefCode }}</span>
        </div>
        <div
          v-if="data.files && data.files.length > 0"
          class="info-row gr-files-row"
        >
          <span class="info-label">ไฟล์ใบรับสินค้า:</span>
          <div class="gr-files-list">
            <GrFileChip
              v-for="(file, index) in data.files"
              :key="file.id || index"
              :filename="file.originalName"
              :show-delete="false"
              @click="previewFile(file.id)"
            />
          </div>
        </div>
        <div class="info-row">
          <span class="info-label">วันที่ทำรายการ:</span>
          <span class="info-value">{{ formatDate(data.createdAt) }}</span>
        </div>
        <div
          v-if="showRejectReason"
          class="reject-reason-box"
        >
          <div class="reject-reason-title">เหตุผลที่ถูกปฏิเสธ:</div>
          <div>{{ data.rejectReason }}</div>
        </div>
        <div class="info-row">
          <span class="info-label">วันที่รับสินค้า:</span>
          <span class="info-value">{{
            data.receivedDt ? formatDate(data.receivedDt) : "-"
          }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">รหัสผู้ขาย:</span>
          <span class="info-value">{{ data.vendorCode }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ชื่อผู้ขาย:</span>
          <span class="info-value">{{ data.vendorName }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ชื่อสาขา:</span>
          <span class="info-value">{{ data.branchName || "-" }}</span>
        </div>
      </div>

      <!-- Right: items table -->
      <div class="gr-detail-table">
        <b-table
          id="grDetailTable"
          hover
          small
          responsive
          head-variant="light"
          :items="sortedItems"
          :fields="fields"
        >
          <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
          <template v-slot:cell(image)="row">
            <ProductImage :src="row.item.imageUrl || null" />
          </template>
          <template v-slot:cell(productCode)="row">
            {{ row.item.productCode || row.item.product?.no || "-" }}
          </template>
          <template v-slot:cell(productName)="row">
            <span
              v-if="row.item.productType === 'FREE'"
              class="free-label"
              >[ของแถม]</span
            >{{
              row.item.productNameMain || row.item.product?.description || "-"
            }}
          </template>
          <template v-slot:cell(quantity)="row">
            {{ formatQty(row.item) }}
          </template>
          <template v-slot:cell(uomName)="row">
            {{ formatUom(row.item) }}
          </template>
          <template v-slot:cell(totalPrice)="row">
            {{ formatNumber(row.item.totalPrice) }}
          </template>
          <template v-slot:cell(lotNumber)="row">
            {{ row.item.lotNumber || "-" }}
          </template>
          <template v-slot:cell(expiredDt)="row">
            {{ row.item.expiredDt ? formatDate(row.item.expiredDt) : "-" }}
          </template>
        </b-table>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { THAI_MONTHS } from "@/constants";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import GrFileChip from "@/components/modal/ProductAndStock/GrFileChip.vue";
import { formatQtyWithBase, formatUomWithBase } from "@/utils/uomConversion";

export default {
  name: "GoodsReceiveDetail",
  components: { ProductImage, GrFileChip },
  data() {
    return {
      isLoading: false,
      data: {
        grCode: "",
        poRefCode: "",
        createdAt: "",
        receivedDt: "",
        vendorCode: "",
        vendorName: "",
        branchName: "",
        status: "",
        rejectReason: null,
        files: [],
        items: [],
      },
      fields: [
        {
          key: "index",
          label: "",
          thStyle: { width: "40px", minWidth: "40px" },
          class: "text-center",
        },
        {
          key: "image",
          label: "",
          thStyle: { width: "50px", minWidth: "50px" },
        },
        {
          key: "productCode",
          label: "รหัส",
          thStyle: { minWidth: "100px" },
        },
        {
          key: "productName",
          label: "ชื่อสินค้า",
          thStyle: { minWidth: "160px" },
          tdClass: "product-name-cell",
        },
        {
          key: "quantity",
          label: "จำนวนสินค้า",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        {
          key: "uomName",
          label: "หน่วยสินค้า",
          thStyle: { minWidth: "100px" },
        },
        {
          key: "totalPrice",
          label: "ราคารวม (บาท)",
          thStyle: { minWidth: "120px" },
          class: "text-right",
        },
        {
          key: "lotNumber",
          label: "เลขล็อต",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        {
          key: "expiredDt",
          label: "วันหมดอายุ",
          thStyle: { minWidth: "120px" },
          class: "text-right",
        },
      ],
    };
  },
  computed: {
    ...mapGetters({
      productList: "moduleProductInventory/getProductList",
    }),
    showRejectReason() {
      return this.data.status === "REJECTED" && !!this.data.rejectReason;
    },
    sortedItems() {
      const items = this.data.items || [];
      const withImage = items.map(item => ({
        ...item,
        imageUrl:
          this.productList.find(p => p.productId === item.productId)
            ?.imageUrl || null,
      }));
      return [
        ...withImage.filter(i => i.productType !== "FREE"),
        ...withImage.filter(i => i.productType === "FREE"),
      ];
    },
  },
  methods: {
    ...mapActions({
      previewMediaFile: "moduleGoodsReceive/previewMediaFile",
    }),
    formatQty(item) {
      return formatQtyWithBase(item.quantity, item.qtyBaseUom);
    },
    formatUom(item) {
      return formatUomWithBase(
        item.uomName || item.uom?.description,
        item.baseUomName
      );
    },
    showToast(message, title, variant = "danger", delay = 3000) {
      this.$root.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
        toaster: "b-toaster-bottom-left",
        autoHideDelay: delay,
      });
    },
    show(goodsReceive) {
      // Show modal with list data, then fetch full details
      this.data = {
        grCode: goodsReceive.grCode || "",
        poRefCode: goodsReceive.poRefCode || "",
        createdAt: goodsReceive.createdAt || "",
        receivedDt: goodsReceive.receivedDt || "",
        vendorCode: goodsReceive.vendorCode || "",
        vendorName: goodsReceive.vendorName || "",
        branchName: goodsReceive.branchName || "-",
        files: [],
        items: [],
      };
      this.$bvModal.show("goodsReceiveDetailModal");
      this.fetchDetail(goodsReceive.id);
    },
    fetchDetail(id) {
      this.isLoading = true;
      this.$store
        .dispatch("moduleGoodsReceive/fetchGoodsReceiveById", {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          id,
        })
        .then(detail => {
          if (detail) {
            this.data = detail;
          }
        })
        .catch(err => {
          console.error("fetchGoodsReceiveById failed", err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    close() {
      this.$bvModal.hide("goodsReceiveDetailModal");
    },
    async previewFile(id) {
      const result = await this.previewMediaFile({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        id,
      });
      if (result.success) return;
      if (result.reason === "not_found") {
        this.showToast("ไม่พบไฟล์", "ไม่สำเร็จ");
      } else if (result.reason === "error") {
        this.showToast("เปิดไฟล์ไม่สำเร็จ", "ไม่สำเร็จ");
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const day = date.getDate();
      const month = THAI_MONTHS[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);
      return `${day} ${month} ${year}`;
    },
    formatNumber(val) {
      if (val === null || val === undefined || val === "") return "-";
      return Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.gr-detail-layout {
  display: flex;
  gap: 24px;
  max-height: 70vh;
}

.gr-detail-info {
  flex: 0 0 320px;
  min-width: 0;
  position: sticky;
  top: 0;
  align-self: flex-start;
  border-right: 1px solid #dee2e6;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gr-detail-table {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: auto;
  max-height: 70vh;
}

.info-row {
  /* margin-bottom: 8px; */
  line-height: 1.6;
  display: flex;
}

.info-label {
  font-weight: 600;
  color: #000;
  white-space: nowrap;
}

.free-label {
  margin-right: 4px;
  color: #212529;
}

.info-value {
  margin-left: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.reject-reason-box {
  background: #FFD9DD;
  border-radius: 4px;
  padding: 16px;
}

.reject-reason-title {
  font-weight: 600;
  font-size: 16px;
  color: #DD3333;
  margin-bottom: 4px;
}

.gr-files-row {
  flex-direction: column;
  gap: 8px;
}

.gr-files-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

::v-deep #grDetailTable {
  table-layout: auto;
}

::v-deep #grDetailTable thead th {
  font-size: 0.85rem;
  white-space: nowrap;
}

::v-deep #grDetailTable tbody td {
  vertical-align: middle;
  font-size: 0.9rem;
  word-break: break-word;
  white-space: normal;
}

::v-deep #goodsReceiveDetailModal .modal-dialog {
  max-width: 1400px;
}

@media (max-width: 768px) {
  ::v-deep .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  .gr-detail-layout {
    flex-direction: column;
    max-height: none;
  }

  .gr-detail-info {
    flex: none;
    position: static;
  }

  .gr-detail-table {
    max-height: 50vh;
  }

  ::v-deep #grDetailTable {
    table-layout: auto;
  }
}
</style>
